<template>
  <div class="record-page">
    <div class="record-shell">
      <header class="record-head">
        <button type="button" class="head-btn" :aria-label="$t('auth.back')" @click="back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <div class="head-copy">
          <h1>{{ copy.title }}</h1>
          <p>{{ copy.subtitle }}</p>
        </div>

        <button type="button" class="head-btn" :aria-label="$t('auth.home')" @click="goHome">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 11l9-8 9 8"></path>
            <path d="M5 10v10h14V10"></path>
          </svg>
        </button>
      </header>

      <main class="record-content">
        <div v-if="!list.length" class="empty-state">
          <div class="empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <rect x="3.5" y="7" width="3.2" height="11" rx="1"></rect>
              <rect x="8.1" y="4" width="3.2" height="14" rx="1"></rect>
              <rect x="12.7" y="8" width="3.2" height="10" rx="1"></rect>
              <rect x="17.3" y="5.5" width="3.2" height="12.5" rx="1"></rect>
            </svg>
          </div>
          <p>{{ copy.emptyText }}</p>
        </div>

        <div v-else class="record-list">
          <button
            v-for="item in list"
            :key="item.id || item.expect"
            type="button"
            class="record-card"
            @click="openDetail(item)"
          >
            <div class="card-top">
              <div class="card-title">
                <b>{{ item.name || copy.defaultType }}</b>
                <small>{{ formatExpect(item.expect) }}</small>
              </div>
              <span class="status-pill" :class="getStatusClass(item)">
                {{ getStatusText(item) }}
              </span>
            </div>

            <div class="card-lines">
              <div class="line-row">
                <span>{{ copy.amountLabel }}</span>
                <b>{{ formatMoney(item.money) }}</b>
              </div>
              <div class="line-row">
                <span>{{ copy.countLabel }}</span>
                <b>{{ getRecordCount(item) }}</b>
              </div>
              <div class="line-row">
                <span>{{ copy.betLabel }}</span>
                <b>{{ getBetSummary(item) }}</b>
              </div>
              <div class="line-row">
                <span>{{ copy.timeLabel }}</span>
                <b>{{ item.create_time || "--" }}</b>
              </div>
            </div>
          </button>
        </div>
      </main>
    </div>

    <div v-if="detailVisible" class="modal-mask" @click.self="closeDetail">
      <div class="modal-card">
        <h3>{{ copy.detailTitle }}</h3>

        <div class="detail-list">
          <div v-if="activeRecord && activeRecord.expect" class="detail-row">
            <span>{{ copy.expectLabel }}</span>
            <b>{{ formatExpect(activeRecord.expect) }}</b>
          </div>
          <div class="detail-row">
            <span>{{ copy.typeLabel }}</span>
            <b>{{ (activeRecord && activeRecord.name) || copy.defaultType }}</b>
          </div>
          <div class="detail-row">
            <span>{{ copy.betLabel }}</span>
            <b>{{ getBetSummary(activeRecord) }}</b>
          </div>
          <div class="detail-row">
            <span>{{ copy.drawLabel }}</span>
            <b>{{ getDrawSummary(activeRecord) }}</b>
          </div>
          <div class="detail-row">
            <span>{{ copy.timeLabel }}</span>
            <b>{{ (activeRecord && activeRecord.create_time) || "--" }}</b>
          </div>
        </div>

        <button type="button" class="modal-btn" @click="closeDetail">
          {{ copy.closeText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export default {
  data() {
    return {
      list: [],
      detailVisible: false,
      activeRecord: null,
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "gameRecord");
    },
    lotteryCopy() {
      return getPageCopy(this, "lottery");
    },
  },
  created() {
    if (!localStorage.getItem("token")) {
      this.$toast(this.copy.loginFirst);
      this.$router.replace("/Login");
      return;
    }
    this.getUserGameList();
  },
  mounted() {
    document.title = `${this.copy.title} · OMN`;
  },
  methods: {
    async getUserGameList() {
      const res = await this.$http({
        method: "get",
        url: "user_get_game_list",
      });
      if (res && res.code === 200) {
        const list = Array.isArray(res.data) ? res.data : Object.values(res.data || {});
        this.list = list;
        return;
      }
      if (res && res.msg) {
        this.$toast(res.msg);
      }
    },
    back() {
      window.history.back();
    },
    goHome() {
      this.$router.push("/Home");
    },
    formatMoney(value) {
      return toNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatExpect(value) {
      return value ? String(value) : "--";
    },
    getStatusText(item) {
      if (!item) {
        return this.copy.waitingText;
      }
      if (Number(item.status) === 0) {
        return this.copy.waitingText;
      }
      if (Number(item.is_win) === 1) {
        return this.copy.winText;
      }
      if (Number(item.is_win) === 2) {
        return this.copy.loseText;
      }
      return this.copy.settledText;
    },
    getStatusClass(item) {
      if (!item) {
        return "wait";
      }
      if (Number(item.status) === 0) {
        return "wait";
      }
      if (Number(item.is_win) === 1) {
        return "ok";
      }
      if (Number(item.is_win) === 2) {
        return "bad";
      }
      return "ok";
    },
    getBetSummary(item) {
      const entries = this.getDetailEntries(item);
      if (!entries.length) {
        return "--";
      }
      const text = entries
        .map((entry) => this.getEntryBetName(entry))
        .filter(Boolean)
        .join(" / ");
      return text || "--";
    },
    getRecordCount(item) {
      const count = Number(item && item.count);
      if (Number.isFinite(count) && count > 0) {
        return count;
      }
      return this.getDetailEntries(item).length || 0;
    },
    getDetailEntries(item) {
      if (!item) {
        return [];
      }
      if (Array.isArray(item.data)) {
        return item.data;
      }
      if (typeof item.data === "string" && item.data.trim()) {
        return item.data
          .split(",")
          .map((value) => ({ type: value.trim() }))
          .filter((entry) => entry.type);
      }
      if (item.type || item.play || item.name) {
        return [item];
      }
      return [];
    },
    getEntryBetName(entry) {
      if (!entry) {
        return "";
      }
      const rawName = [entry.type, entry.play, entry.name, entry.expect]
        .find((value) => typeof value === "string" && value.trim());
      if (!rawName) {
        return "";
      }
      return this.translateLotteryBetName(rawName.trim());
    },
    translateLotteryBetName(name) {
      const giftNameMap = (this.lotteryCopy && this.lotteryCopy.giftNameMap) || {};
      const backendMap =
        (this.lotteryCopy && this.lotteryCopy.backendGiftTypeMap) || {};
      if (giftNameMap[name]) {
        return giftNameMap[name];
      }
      if (backendMap[name]) {
        return backendMap[name];
      }
      // 英文环境兜底：后台仍返回中文礼物名时映射为 Rose/Heart/Ring/Car
      const fallback = {
        玫瑰: "Rose",
        漂亮: "Rose",
        爱心: "Heart",
        愛心: "Heart",
        性感: "Heart",
        百合: "Heart",
        钻戒: "Ring",
        鑽戒: "Ring",
        迷人: "Ring",
        烟花: "Ring",
        煙花: "Ring",
        跑车: "Car",
        跑車: "Car",
        可爱: "Car",
        可愛: "Car",
        气球: "Car",
        氣球: "Car",
        pretty: "Rose",
        sexy: "Heart",
        charming: "Ring",
        cute: "Car",
      };
      const locale =
        (this.$i18n && this.$i18n.locale) ||
        localStorage.getItem("lang") ||
        "";
      if (String(locale).toLowerCase().indexOf("zh") === 0) {
        return name;
      }
      return fallback[name] || name;
    },
    getDrawSummary(item) {
      if (!item) {
        return "--";
      }
      if (Array.isArray(item.opencode) && item.opencode.length) {
        return item.opencode.join(", ");
      }
      return item.update_time || this.copy.pendingDrawText;
    },
    openDetail(item) {
      this.activeRecord = item;
      this.detailVisible = true;
    },
    closeDetail() {
      this.detailVisible = false;
      this.activeRecord = null;
    },
  },
};
</script>

<style lang="less" scoped>
.record-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 100% 0, rgba(255, 63, 120, 0.11), transparent 28%),
    radial-gradient(circle at 0 14%, rgba(165, 92, 255, 0.1), transparent 26%),
    #0a0710;
  color: #fff7fb;
}

.record-shell {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  max-width: none;
  margin: 0;
}

.record-head {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: 2.625rem 1fr 2.625rem;
  align-items: center;
  min-height: 4.1875rem;
  padding: max(0.5rem, env(safe-area-inset-top)) 0.8125rem 0.5rem;
  background: rgba(10, 7, 16, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
  backdrop-filter: blur(1.125rem);
  -webkit-backdrop-filter: blur(1.125rem);
}

.head-btn {
  width: 2.375rem;
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
}

.head-btn svg {
  width: 1.1875rem;
  height: 1.1875rem;
}

.head-copy h1 {
  margin: 0;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 900;
  letter-spacing: -0.0125rem;
}

.head-copy p {
  margin: 0.1875rem 0 0;
  text-align: center;
  color: #a89baa;
  font-size: 0.5625rem;
  line-height: 1.4;
}

.record-content {
  padding: 1rem;
}

.empty-state {
  min-height: calc(100vh - 8rem);
  min-height: calc(100dvh - 8rem);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  color: #a596a2;
}

.empty-icon {
  width: 4.0625rem;
  height: 4.0625rem;
  margin-bottom: 0.75rem;
  border-radius: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.085);
  color: #7a4a62;
}

.empty-icon svg {
  width: 1.75rem;
  height: 1.75rem;
}

.empty-state p {
  margin: 0;
  font-size: 0.8125rem;
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 0.6875rem;
}

.record-card {
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.375rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.045);
  box-shadow: 0 0.8125rem 2.125rem rgba(0, 0, 0, 0.15);
  color: #fff7fb;
  text-align: left;
}

.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.625rem;
  padding-bottom: 0.625rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
}

.card-title {
  min-width: 0;
}

.card-title b {
  display: block;
  font-size: 0.8125rem;
  line-height: 1.4;
}

.card-title small {
  display: block;
  margin-top: 0.25rem;
  color: #a89baa;
  font-size: 0.625rem;
}

.status-pill {
  height: 1.5rem;
  padding: 0 0.5rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  font-size: 0.5625rem;
  font-weight: 900;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.065);
  color: #c9bdc7;
}

.status-pill.ok {
  background: rgba(77, 227, 165, 0.1);
  color: #67e7b3;
}

.status-pill.wait {
  background: rgba(255, 198, 92, 0.1);
  color: #ffd071;
}

.status-pill.bad {
  background: rgba(255, 63, 120, 0.1);
  color: #ff85a8;
}

.card-lines {
  padding-top: 0.4375rem;
}

.line-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.875rem;
  padding: 0.3125rem 0;
  font-size: 0.625rem;
}

.line-row span {
  color: #a89baa;
  flex: 0 0 auto;
}

.line-row b {
  color: #eee5ec;
  font-size: 0.625rem;
  font-weight: 750;
  text-align: right;
  word-break: break-word;
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(3, 2, 5, 0.76);
  backdrop-filter: blur(0.5625rem);
}

.modal-card {
  width: 100%;
  max-width: 24.375rem;
  border-radius: 1.5rem;
  padding: 1.25rem;
  background: #1a1221;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 1.375rem 4.375rem rgba(0, 0, 0, 0.5);
}

.modal-card h3 {
  margin: 0 0 0.625rem;
  font-size: 1.125rem;
  font-weight: 900;
}

.detail-list {
  margin-top: 0.25rem;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6875rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.detail-row:last-child {
  border-bottom: 0;
}

.detail-row span {
  color: #a89baa;
  flex: 0 0 auto;
}

.detail-row b {
  text-align: right;
  font-weight: 800;
  color: #fff7fb;
  word-break: break-word;
}

.modal-btn {
  width: 100%;
  height: 2.6875rem;
  margin-top: 1rem;
  border: 0;
  border-radius: 0.9375rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 900;
}

button {
  cursor: pointer;
}

@media (min-width: 48rem) {
  .record-shell {
    max-width: 25rem;
    margin: 0 auto;
  }
}
</style>
